import { useRef, useState } from 'react';
import { SignaturePad, type SignaturePadHandle } from '../components/signature-pad';
import { Banner, BigButton, Empty, Label, Screen, TextArea, TextField } from '../components/ui';
import { saveSignature } from '../lib/actions';
import { db, useLive } from '../lib/db';
import { electronicOrders } from '../lib/requirements';
import { navigate } from '../lib/router';
import { useStop, useStops } from '../lib/selectors';

/**
 * Подпис на получателя. Пътят „със забележки" не е украса: текстът, който
 * получателят диктува тук („два кашона намачкани, стрейч разкъсан"), е
 * доказателството при рекламация за щета. Затова се записва дословно и с час.
 */
export function SignatureScreen({ stopId }: { stopId: string }) {
  const stop = useStop(stopId);
  const tripStops = useStops(stop?.tripId ?? null);
  const events = useLive(
    async () => (stop?.tripId ? db.events.where('tripId').equals(stop.tripId).toArray() : []),
    [stop?.tripId],
    [],
  );
  const padRef = useRef<SignaturePadHandle>(null);
  const [receiver, setReceiver] = useState('');
  const [withNotes, setWithNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!stop) {
    return (
      <Screen title="Подпис" back>
        <Empty>Този адрес вече не е в курса.</Empty>
      </Screen>
    );
  }

  async function submit() {
    const canvas = padRef.current?.canvas();
    if (!stop) return;
    if (receiver.trim().length < 3) {
      setError('Напишете името на човека, който приема стоката.');
      return;
    }
    if (!canvas || padRef.current?.isEmpty()) {
      setError('Нужен е подпис в бялото поле.');
      return;
    }
    if (withNotes && notes.trim().length < 3) {
      setError('Напишете какво е забелязал получателят.');
      return;
    }
    setBusy(true);
    try {
      await saveSignature({
        canvas,
        tripId: stop.tripId,
        orderStopId: stop.id,
        receiverName: receiver.trim(),
        reservations: withNotes ? notes.trim() : null,
      });
      navigate('/', true);
    } catch (issue) {
      setError(issue instanceof Error ? issue.message : 'Подписът не можа да се запише');
      setBusy(false);
    }
  }

  return (
    <Screen title="Подпис на получателя" back>
      <div className="space-y-4">
        <p className="text-xl text-muted">
          {stop.name}, {stop.city}
        </p>
        <p className="text-lg text-muted">
          {stop.orderId && electronicOrders(tripStops, events).has(stop.orderId)
            ? 'Това е и подписът по електронната ЧМР (кл. 24) — отделна хартия не е нужна.'
            : 'Подписът потвърждава доставката. Хартиената ЧМР с подписа и печата на получателя вече е заснета.'}
        </p>

        <TextField
          label="Име и фамилия на получателя"
          value={receiver}
          onChange={setReceiver}
          placeholder="Например: Мартин Хубер"
        />

        <div>
          <Label>Подпис с пръст</Label>
          <div className="mt-1">
            <SignaturePad ref={padRef} />
          </div>
          <button
            type="button"
            onClick={() => padRef.current?.clear()}
            className="tap mt-2 w-full rounded-2xl border-2 border-line px-4 py-3 text-lg font-semibold"
          >
            Изтрий и започни отново
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setWithNotes(false)}
            className={`tap rounded-2xl border-2 px-3 py-4 text-lg font-bold ${
              withNotes ? 'border-line bg-card' : 'border-go bg-go text-white'
            }`}
          >
            Без забележки
          </button>
          <button
            type="button"
            onClick={() => setWithNotes(true)}
            className={`tap rounded-2xl border-2 px-3 py-4 text-lg font-bold ${
              withNotes ? 'border-warn bg-warn text-white' : 'border-line bg-card'
            }`}
          >
            Със забележки
          </button>
        </div>

        {withNotes ? (
          <>
            <TextArea
              label="Какво казва получателят"
              value={notes}
              onChange={setNotes}
              placeholder="Например: два кашона намачкани в горния ъгъл, стрейчът разкъсан"
              rows={5}
            />
            <Banner tone="warn">
              Запишете забележката така, както я казва получателят. Този текст се използва при
              рекламация за щета.
            </Banner>
          </>
        ) : null}

        {error ? <Banner tone="stop">{error}</Banner> : null}

        <BigButton tone="go" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Записва се…' : 'Запиши подписа'}
        </BigButton>
      </div>
    </Screen>
  );
}
